import type { AsyncDataOptions, NuxtApp } from '#app';
import type { CondoPriceRange } from '~/core/entities/condo/Condo';
import type { PropertyFilters } from '~/core/entities/property/filters';
import type { CurrencyRange } from '~~/type-utils';
import type { ClassValue } from 'clsx';

import { useDebounceFn } from '@vueuse/core';
import {
	PropertyTypePlurals,
	propertyTypesTranslations
} from '~/core/entities/CRMEnums';
import { formatCurrency, grammarInflection } from '~~/utils';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export const CACHE_TIME = 60 * 60 * 1000;

export function waitUntilFalse(
	source: () => boolean,
	timeoutMs = 10_000
): Promise<boolean> {
	if (!source()) return Promise.resolve(true);
	return new Promise(resolve => {
		const stop = watch(
			source,
			val => {
				if (!val) {
					cleanup();
					resolve(true);
				}
			},
			{ immediate: true }
		);

		const timer = setTimeout(() => {
			cleanup();
			console.warn(
				`[waitUntilFalse] Timed out after ${timeoutMs}ms — source is still truthy`
			);
			resolve(false);
		}, timeoutMs);

		function cleanup() {
			stop();
			clearTimeout(timer);
		}
	});
}

export function useStaleGuard() {
	let id = 0;
	function next() {
		return ++id;
	}
	function isStale(capturedId: number) {
		return capturedId !== id;
	}
	return { next, isStale };
}

export function cn(...inputs: ClassValue[]) {
	return twMerge(clsx(inputs));
}

export function normalizeString(str: string = '', avoidChars: string[] = []) {
	const avoidCharsString = avoidChars.join('');
	return str?.trim()
		? str
				.toLowerCase()
				.normalize('NFD')
				.replace(/\p{Diacritic}/gu, '')
				.replace(new RegExp(`[^a-z0-9${avoidCharsString}]`, 'gim'), '')
				.trim()
		: '';
}

export function deduplicateRegions(regions: string[]): string[] {
	const uniqueRegions = new Map<string, string>();

	regions.forEach(region => {
		const normalized = region
			.toLowerCase()
			.normalize('NFD')
			.replace(/[\u0300-\u036f]/g, '');

		const existing = uniqueRegions.get(normalized);
		if (existing === undefined) {
			uniqueRegions.set(normalized, region);
		} else {
			const existingUpperCount = (existing.match(/[A-Z]/g) || []).length;
			const currentUpperCount = (region.match(/[A-Z]/g) || []).length;
			if (currentUpperCount > existingUpperCount) {
				uniqueRegions.set(normalized, region);
			}
		}
	});

	return Array.from(uniqueRegions.values());
}

export function clampNumber(number: number, min: number, max: number): number {
	return Math.min(Math.max(number, min), max);
}

export function convertCurrencyValueToFloat(val: string | number = '') {
	const stringTrusted = String(val);

	if (stringTrusted.includes(',')) {
		const stringFormatted = stringTrusted.replaceAll('.', '').replace(',', '.');

		return val ? parseFloat(Number(stringFormatted).toFixed(2)) : 0;
	}
	return parseFloat(Number(stringTrusted).toFixed(2));
}

export function floatToLocaleString(
	floatCurrency: number = 0,
	config: Intl.NumberFormatOptions = {}
) {
	const floatCurrencyFixed = convertCurrencyValueToFloat(floatCurrency);
	const roundedValue = Math.ceil(floatCurrencyFixed);
	return new Intl.NumberFormat('pt-BR', {
		style: 'currency',
		currency: 'BRL',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0,
		...config
	}).format(roundedValue);
}

export function replaceNbspWithSpace(text: string): string {
	return text.replace(/\u00A0/g, ' ');
}

export function getCarouselMaskedCarouselItemMargin(index: number) {
	return {
		marginLeft: index > 0 ? '-100%' : '0',
		zIndex: '20'
	};
}

export const uniqueId = (
	counter =>
	(str = '') =>
		`${str}${++counter}`
)(0);

export function capitalize(value: string): string {
	return value.charAt(0).toUpperCase() + value.slice(1);
}

const removeTrailingSlashes = (str: string) => str.replace(/\/+$/, '');
const removeLeadingSlashes = (str: string) => str.replace(/^\/+/, '');

export function concatUrlPath(url: string, path: string): string {
	return `${removeTrailingSlashes(url)}/${removeLeadingSlashes(path)}`;
}

type CacheConfig<T> =
	| Record<string, never>
	| Pick<AsyncDataOptions<T, T, any, T>, 'transform' | 'getCachedData'>;

export function getCacheAsyncDataConfig<T extends Record<string, any> | null>(
	cacheTime: number = 30 * 1000 // 1 minute
): CacheConfig<T> {
	return {
		// transform: (data: T) => {
		// 	return {
		// 		...data,
		// 		fetchedAt: new Date().toISOString()
		// 	};
		// },
		// getCachedData: (key: string, nuxtApp: NuxtApp): T | undefined => {
		// 	if (import.meta.server) {
		// 		console.log('getCachedData server side');
		// 		const testData = nuxtApp.payload?.data || nuxtApp.static?.data;
		// 		const size = Buffer.byteLength(JSON.stringify(testData), 'utf8');
		// 		console.log(`Server Payload Size: ${(size / 1024).toFixed(2)} KB`);
		// 	} else if (import.meta.client) {
		// 		// console.log('getCachedData client side');
		// 	}
		// 	const data = nuxtApp.payload?.data[key] || nuxtApp.static?.data[key];
		// 	if (!data) return undefined;
		// 	const fetchedAt = data.fetchedAt ? new Date(data.fetchedAt) : null;
		// 	if (!fetchedAt) return undefined;
		// 	const expirationDate = new Date(fetchedAt.getTime() + cacheTime);
		// 	const isExpired = expirationDate.getTime() < Date.now();
		// 	if (isExpired) {
		//     delete nuxtApp.payload?.data[key];
		//     delete nuxtApp.static?.data[key];
		//     return undefined;
		//   }
		// 	return data as T;
		// }
	};
}

export function validateSpecialCharacters(value: string) {
	const pattern =
		/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>/?°º¹²³£¢¬§`~\u00C0-\u00FF]/;

	const hasSpecialCharacters = pattern.test(value);

	const foundSpecialChars = value.match(pattern) || [];

	return {
		hasSpecialCharacters,
		specialCharacters: [...new Set(foundSpecialChars)],
		originalString: value
	};
}

export function getPropertyTypeText(
	filters: PropertyFilters,
	count: number
): string {
	if (filters.propertyTypes?.length === 1) {
		const propertyType = filters.propertyTypes[0]!;

		return count > 1
			? PropertyTypePlurals[propertyType]
			: propertyTypesTranslations[propertyType];
	}

	return grammarInflection(count, 'imóvel', 'imóveis');
}

/**
 * Generates ALT text for property images following the format:
 * "{PropertyType} com {suites} suítes à venda em {region}, {city}, por R$ {askingPrice} [Imagem X]"
 * The first image (index 0) doesn't include the "Imagem X" suffix.
 *
 * @param propertyType - The type of property (e.g., "Apartamento", "Casa")
 * @param suites - Number of suites in the property
 * @param region - The region where the property is located
 * @param city - The city where the property is located
 * @param askingPrice - The asking price of the property
 * @param imageIndex - Optional image index to append to the ALT text, should be 0-based
 * @returns Formatted ALT text for the property image
 */
export function getPropertyImageAlt(
	propertyType?: string | null,
	suites?: number | null,
	region?: string | null,
	city?: string | null,
	askingPrice?: number | null,
	imageIndex?: number | null
): string {
	if (!propertyType || !suites || !region || !city || !askingPrice) {
		return imageIndex !== undefined && imageIndex !== null
			? `Property Image ${imageIndex}`
			: 'Property Image';
	}

	const formattedPrice = floatToLocaleString(askingPrice, {
		style: 'currency',
		minimumFractionDigits: 0,
		maximumFractionDigits: 0
	})
		.replace('R$', '')
		.trim();

	const baseAlt = `${propertyType} com ${suites} suítes à venda em ${region}, ${city}, por R$ ${formattedPrice}`;

	// First image (index 0) doesn't include the "Imagem X" suffix
	if (imageIndex === 0) {
		return baseAlt;
	}

	return imageIndex !== undefined && imageIndex !== null
		? `${baseAlt} Imagem ${imageIndex + 1}`
		: baseAlt;
}

/**
 * Generates ALT text for condominium images following the format:
 * "{condoName}" for the first image (index 0) or
 * "{condoName} Imagem X" for subsequent images
 *
 * @param condoName - The name of the condominium
 * @param index - Zero-based index of the image in the gallery
 * @returns Formatted ALT text for the condominium image
 */
export function getCondoImageAlt(condoName: string, index: number): string {
	return index === 0 ? condoName : `${condoName} Imagem ${index}`;
}

export function isInstagramBrowserAgent() {
	const userAgent = navigator?.userAgent || '';
	return /Instagram/.test(userAgent);
}

/**
 * Validates if a range object has at least one valid value (min or max)
 * @param range - An object containing min and max values, or null
 * @param range.min - The minimum value of the range
 * @param range.max - The maximum value of the range
 * @returns {boolean} True if the range has at least one non-null value, false otherwise
 * @example
 */
export function validateRange(
	range: { min: number | null; max: number | null } | null
) {
	if (!range) return false;

	const values = Object.values(range);

	return values.some(Boolean);
}

/**
 * Formats a number to a localized string with 2 decimal places
 * @param value - The number to format
 * @returns A string representation of the number with 2 decimal places
 */
export function getFormattedSquareMeter(value: number): string {
	if (!value) return '';
	const parsedSquareMeter = floatToLocaleString(value, { style: 'decimal' });

	return `${parsedSquareMeter}m²`;
}

/**
 * Formats a number as a currency string
 * @param value - The number to format as currency
 * @returns A formatted currency string (e.g., "R$ 1.000,00")
 */
export function getFormattedCurrency(value: number): string {
	if (!value) return '';

	return formatCurrency(value).replace(',', '.');
}

/**
 * Formats a number as a square meter price string
 * @param value - The price per square meter to format
 * @returns A formatted string with currency and square meter unit (e.g., "R$ 1.000,00/m²")
 */
export function getFormattedSquareMeterPrice(value: number): string {
	if (!value) return '';

	const parsedSquareMeter = floatToLocaleString(value);

	return `${parsedSquareMeter}/m²`;
}

/**
 * Formats a range object with min and max values into currency strings
 * @param range Object containing min and max number values
 * @param defaultValue Optional default value if range is null/undefined (defaults to 0)
 * @returns Object with formatted currency strings for min and max
 * @example
 * formatCurrencyRange({ min: 1000, max: 2000 }) => { min: "R$ 1.000,00", max: "R$ 2.000,00" }
 */
export function formatRange(
	range: CondoPriceRange | undefined,
	parser: typeof getFormattedCurrency | typeof getFormattedSquareMeter,
	defaultValue: number = 0
): CurrencyRange {
	return {
		min: parser(range?.min ?? defaultValue),
		max: parser(range?.max ?? defaultValue)
	};
}

/**
 * Formats an array of numbers into a string
 * @param numbers Array of numbers to format
 * @returns Formatted string with numbers separated by commas and 'e' for the last item
 */
export function formatNumbersArray(numbers: number[] = []): string {
	if (!numbers.length) return '';
	if (numbers.length === 1) return numbers[0]!.toString();
	if (numbers.length === 2) return `${numbers[0]} e ${numbers[1]}`;

	const lastNumber = numbers[numbers.length - 1];
	const allButLast = numbers.slice(0, -1).join(', ');

	return `${allButLast} e ${lastNumber}`;
}

export function formatNumberArrayWithGrammarInflection(
	numbers: number[] = [],
	word: string,
	pluralWord: string
): string {
	if (!numbers.length) return '';

	const formattedNumbers = formatNumbersArray(numbers);

	if (numbers.length && numbers.some(num => num > 1)) {
		return `${formattedNumbers} ${pluralWord}`;
	}

	if (numbers.length === 1 && numbers[0]) {
		return `${formattedNumbers} ${grammarInflection(numbers[0], word, pluralWord)}`;
	}

	const label = grammarInflection(numbers.length, word, pluralWord);

	return `${formattedNumbers} ${label}`;
}

/**
 * Builds a formatted address slug from condominium data
 * Format: [address], [number], [region], [city] - [state]
 * Avoids duplicating region if already present in address
 */
export function buildAddressSlug(condoData: {
	address?: string;
	number?: string;
	region?: string;
	city?: string;
	state?: string;
}): string {
	const {
		address = '',
		number,
		region = '',
		city = '',
		state = ''
	} = condoData;

	let addressSlug = address;

	if (number) {
		addressSlug += `, ${number}`;
	}

	if (region && !address.toLowerCase().includes(region.toLowerCase())) {
		addressSlug += `, ${region}`;
	}

	if (city) {
		addressSlug += `, ${city}`;
	}

	if (state) {
		addressSlug += ` - ${state}`;
	}

	return addressSlug;
}

/**
 * Wrapper to handle promise rejections that can reject with undefined
 * (common when useDebounceFn is cancelled)
 */
export function handleDebouncedPromise<T>(
	promise: Promise<T>
): Promise<T | void> {
	return promise.catch(error => {
		// if the error is undefined, it's probably a debounce cancellation
		if (error === undefined) {
			return;
		}

		// If the error is a cancellation error, don't propagate
		if (error?.name === 'CancelledError') {
			return;
		}

		// For other errors, re-throw to be handled by the context
		throw error;
	});
}

/**
 * Creates a safe debounced function that doesn't generate UnhandledRejection
 */
export function createSafeDebouncedFunction<T extends (...args: any[]) => any>(
	fn: T,
	delay: number,
	options?: { maxWait?: number }
): T {
	const debouncedFn = useDebounceFn(fn, delay, {
		...options,
		rejectOnCancel: true
	});

	return ((...args: Parameters<T>) => {
		const result = debouncedFn(...args);

		// If the result is a Promise, apply the error handling
		if (result && typeof result.then === 'function') {
			return handleDebouncedPromise(result);
		}

		// If the result is not a Promise, return the result directly
		return result;
	}) as T;
}

/**
 * Deep comparison function similar to lodash's isEqual
 * Performs a deep comparison between two values to determine if they are equivalent
 * @param a - First value to compare
 * @param b - Second value to compare
 * @returns True if values are equivalent, false otherwise
 */
export function isEqual(a: any, b: any): boolean {
	// Same reference or strict equality
	if (a === b) return true;

	// Handle null and undefined
	if (a == null || b == null) return a === b;

	// Different types
	if (typeof a !== typeof b) return false;

	// Handle primitives (string, number, boolean, symbol, bigint)
	if (typeof a !== 'object') return a === b;

	// Handle Arrays
	if (Array.isArray(a)) {
		if (!Array.isArray(b) || a.length !== b.length) return false;
		return a.every((item, index) => isEqual(item, b[index]));
	}

	// Handle Dates
	if (a instanceof Date && b instanceof Date) {
		return a.getTime() === b.getTime();
	}

	// Handle RegExp
	if (a instanceof RegExp && b instanceof RegExp) {
		return a.toString() === b.toString();
	}

	// Handle other built-in objects that should not be deeply compared
	if (a.constructor !== b.constructor) return false;

	// Handle plain objects
	const keysA = Object.keys(a);
	const keysB = Object.keys(b);

	if (keysA.length !== keysB.length) return false;

	return keysA.every(
		key =>
			Object.prototype.hasOwnProperty.call(b, key) && isEqual(a[key], b[key])
	);
}
